// Deleting a client — the widest destructive action in the system.
//
// Same frame as the job and retainer versions (DeleteConfirmModal, including the
// type-the-name guard), so only what is specific to a CLIENT is here:
//
//   - Retainers and standalone jobs are LISTED BY NAME. They are what a partner
//     recognises as "the work we do for them", and this is the last screen before all
//     of it is gone. The topics inside each retainer are counted on its own line rather
//     than enumerated — a long-standing client would otherwise produce a wall of text,
//     which is a warning nobody reads.
//   - A LEAD is the same row as a client, and usually has nothing beneath it. Its
//     dialog says so rather than showing an empty destruction list with no explanation.
//   - Deactivating is the reversible alternative and is offered here, the way closing
//     is offered on the retainer.
import useAsync from '../../hooks/useAsync';
import { clientsApi } from '../../api';
import he from '../../i18n/he';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';

const lines = (p) => [
  { key: 'retainers', count: p.retainers, label: he.clientCard.deleteRetainers },
  { key: 'jobs', count: p.jobs, label: he.clientCard.deleteJobs },
  { key: 'invoices', count: p.invoices, label: he.common.deleteInvoices, amount: p.invoicedTotal },
  { key: 'payments', count: p.payments, label: he.common.deletePayments, amount: p.collectedTotal },
  { key: 'expenses', count: p.expenses, label: he.common.deleteExpenses, amount: p.expenseTotal },
  { key: 'tasks', count: p.tasks, label: he.common.deleteTasks },
  { key: 'meetings', count: p.meetings, label: he.calendar.deleteMeetings },
  { key: 'notes', count: p.notes, label: he.leads.deleteNotes },
  { key: 'contacts', count: p.contacts, label: he.clients.deleteContacts },

  { key: 'publications', count: p.publications, label: he.common.deletePublications },
  { key: 'files', count: p.files, label: he.common.deleteFiles },
  // Its own line rather than folded into the file count above: a signed contract may be
  // the only copy there is, and "12 קבצים" does not warn about it at all.
  { key: 'agreements', count: p.agreements, label: he.common.deleteAgreements },
  // The work content writers sent in (064). Its own line rather than folded into the
  // file count above, for the same reason the contracts have one: nobody here filed it,
  // and a dialog that says '12 files' warns about somebody else's deliverables not at
  // all.
  { key: 'workFiles', count: p.workFiles, label: he.common.deleteWorkFiles },
].filter((line) => line.count > 0);

const NamedList = ({ heading, rows, render }) => (
  <div className="delete-named-list">
    <p>{heading}</p>
    <ul>
      {rows.map((row) => <li key={row.id}>{render(row)}</li>)}
    </ul>
  </div>
);

export default function DeleteClientModal({ client, onClose, onDeleted }) {
  const { data: preview, loading, error } = useAsync(
    () => clientsApi.deletionPreview(client.id), [client.id]
  );
  const isLead = preview && preview.stage !== 'active' && preview.jobs === 0;

  return (
    <DeleteConfirmModal
      title={he.clientCard.deleteClient}
      loading={loading}
      error={error}
      name={preview?.name}
      scopeNote={preview && preview.jobs > 0 ? he.clientCard.deleteScope : null}
      items={preview ? lines(preview) : []}
      note={preview && (preview.payments > 0 || preview.expenses > 0)
        ? he.clientCard.deleteBalanceNote
        : null}
      onConfirm={() => clientsApi.remove(client.id)}
      onClose={onClose}
      onDeleted={onDeleted}
    >
      {preview?.retainerList.length > 0 && (
        <NamedList
          heading={he.clientCard.deleteRetainersHeading}
          rows={preview.retainerList}
          render={(r) => (
            <>
              {r.title}
              {r.jobs > 0 && (
                <span className="muted"> · {r.jobs} {he.clientCard.deleteTopicsInside}</span>
              )}
            </>
          )}
        />
      )}
      {preview?.standaloneJobList.length > 0 && (
        <NamedList
          heading={he.clientCard.deleteJobsHeading}
          rows={preview.standaloneJobList}
          render={(j) => j.title}
        />
      )}
      {isLead && (
        <p className="muted delete-note">{he.clientCard.deleteLeadNote}</p>
      )}
    </DeleteConfirmModal>
  );
}
